'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    __gtm_last_url?: string; // for simple de-dup
  }
}

// Read at module scope; it's static on the client
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

export default function GTMPageView() {
  const pathname = usePathname();
  const lastUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!GTM_ID) return;

    // Capture query string without useSearchParams (avoids Suspense)
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const url = `${pathname}${search}`;

    // De-dup: prevent double fire in React Strict Mode + same-URL repeats
    const lastPushed = window.__gtm_last_url ?? lastUrlRef.current;
    if (lastPushed === url) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_path: url,
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    });

    window.__gtm_last_url = url; // persist across re-mounts
    lastUrlRef.current = url;
  }, [pathname]);

  return null;
}
